import { PLAYER_SEARCH_MAX_LENGTH, type PlayerSummary, type SalaryPoolPreview } from '@brush/api-client';
import {
  BuilderSection,
  IssueList,
  NumberInput,
  SALARY_SOURCE_LABELS,
  TARGET_POOL_MAXIMUM,
  TARGET_POOL_MINIMUM,
  controlClass,
  formatMoney,
  formatPoints,
  formatSalary,
  ordinal,
  poolSizeReassurance,
  poolSizeWarning,
  smallButtonClass,
  strengthLabel,
  useDebouncedValue,
} from '@brush/ui';
import { useMemo, useState } from 'react';
import { ExportControl } from '../ExportControl';
import { poolProblems, poolRowProvenance, sortPool, type PoolRow } from '../../fantasy/contest';
import { useRoomPlayerSearch } from '../../queries';
import { pickerState } from '../players/pickerState';

/**
 * Section 4 — the pool review, and §9.1 says outright that it is the section needing real care.
 *
 * <b>What it is for: judging prices, not entering them.</b> The model produces a number for every
 * player and an operator cannot check 150 of them. What they *can* check is the handful they have
 * an opinion about — the regular who has stopped turning up, the visitor who cashed twice and will
 * not be back — and to check one they need what the price was built from in front of them. So each
 * row opens onto the figures the model read, and the price beside it is editable with a reason.
 *
 * Four rules this screen keeps, each with a reason worth not re-litigating:
 *
 * - <b>Never a raw percentile</b> (§9.3). A player's standing is five words and their history is
 *   facts — cashes, points, money. The composite is a weighted sum of percentile ranks, which is
 *   not a quantity anybody has intuition about, and printing it would invite an operator to argue
 *   with a number rather than with a price.
 * - <b>An override always carries a reason</b>, and the box appears with the override rather than
 *   after a failed save. `FantasyPoolValidator` refuses a reasonless one server-side; what this
 *   buys is that it reads as a required field instead of a rejected request.
 * - <b>A re-derive never discards an override.</b> That is `mergeDerived`'s job and the whole
 *   reason `IsOverridden` is a column — see its note.
 * - <b>The freeze is stated here, not only in the publish dialog.</b> This is where somebody spends
 *   twenty minutes adjusting prices, and "you cannot change this later" is worth knowing before
 *   that rather than at the confirmation.
 */
export function ContestPoolReview({
  step,
  rows,
  onRowsChange,
  unit,
  rosterSize,
  frozen,
  usingModel,
  lastDerive,
  onDerive,
  deriving,
  deriveDisabledReason,
  roomId,
  contestId,
  contestName,
  contestStatus,
}: {
  step: number;
  rows: readonly PoolRow[];
  onRowsChange: (next: PoolRow[]) => void;
  /** The budget's unit, so a price is never a bare number. */
  unit: string;
  rosterSize: number;
  /** Published or later: the pool is immutable and every control goes. */
  frozen: boolean;
  /** Whether a salary model is chosen — §4.5's derive path versus its manual one. */
  usingModel: boolean;
  /** Counts and warnings from the last derive, for as long as this screen is open. */
  lastDerive: SalaryPoolPreview | null;
  onDerive: () => void;
  deriving: boolean;
  /** Why Derive cannot run, or null. A reason rather than a silently disabled button. */
  deriveDisabledReason: string | null;
  /** The room, whose players are searched for adding somebody the model missed or never saw. */
  roomId: string | null;
  /** F3's export (docs/specs/export.md EX7): the pool as saved, which in a Draft may differ from unsaved edits here. */
  contestId: string;
  contestName: string;
  contestStatus: string;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const problems = useMemo(() => poolProblems(rows), [rows]);

  const overridden = rows.filter((r) => r.isOverridden).length;
  const unproven = rows.filter((r) => r.isUnproven).length;

  const update = (playerId: string, patch: Partial<PoolRow>) =>
    onRowsChange(rows.map((row) => (row.playerId === playerId ? { ...row, ...patch } : row)));

  return (
    <BuilderSection
      step={step}
      title="Pool review"
      summary={
        rows.length === 0
          ? 'Nobody is draftable yet.'
          : `${rows.length} draftable · ${overridden} priced by hand · ${unproven} unproven`
      }
      action={
        <span className="flex flex-wrap items-center gap-2">
          {frozen ? null : (
            <button
              type="button"
              onClick={onDerive}
              disabled={deriving || deriveDisabledReason !== null}
              title={deriveDisabledReason ?? undefined}
              className={smallButtonClass}
            >
              {deriving ? 'Pricing…' : rows.length === 0 ? 'Derive pool' : 'Re-run the model'}
            </button>
          )}
          {/* F3: the pool as saved — every player, display rank then salary. */}
          <ExportControl
            availability={{ view: 'F3', status: contestStatus }}
            target={{ view: 'F3', contestId }}
            viewLabel={`${contestName} — pool`}
            subject={`${contestName} pool`}
          />
        </span>
      }
    >
      <div className="space-y-3">
        {frozen ? (
          <p className="border border-line bg-ground/50 p-3 text-[11.5px] leading-relaxed text-ink-800/70">
            Frozen when this contest published. These are the exact prices entrants drafted against
            and nothing can change them.
          </p>
        ) : (
          <p className="border border-line border-l-4 border-l-signal bg-surface p-3 text-[11.5px] leading-relaxed text-ink-900">
            {/* Signal orange means "you must act" in this app, and this is the one thing on the
                screen that becomes impossible on the next click. */}
            <strong className="text-ink-950">Publishing freezes all of this.</strong> Every player
            below and every price beside them becomes permanent the moment this contest publishes —
            there is no route that edits them afterwards. Get it right here.
          </p>
        )}

        {!frozen && deriveDisabledReason !== null && rows.length === 0 ? (
          <p className="text-[11.5px] leading-relaxed text-ink-800/60">{deriveDisabledReason}</p>
        ) : null}

        {lastDerive ? <DeriveSummary result={lastDerive} /> : null}
        <PoolSizeNotice poolSize={rows.length} rosterSize={rosterSize} />
        <IssueList issues={problems.map((message) => ({ severity: 'error' as const, message }))} />

        {rows.length === 0 ? (
          <EmptyPool frozen={frozen} usingModel={usingModel} />
        ) : (
          <PoolTable
            rows={rows}
            unit={unit}
            frozen={frozen}
            expanded={expanded}
            onToggle={(id) => setExpanded((open) => (open === id ? null : id))}
            onUpdate={update}
            onRemove={(playerId) => onRowsChange(rows.filter((r) => r.playerId !== playerId))}
          />
        )}

        {frozen ? null : (
          <AddPlayer
            roomId={roomId}
            alreadyIn={new Set(rows.map((r) => r.playerId))}
            unit={unit}
            onAdd={(player) =>
              onRowsChange(
                sortPool([
                  ...rows,
                  {
                    playerId: player.id,
                    playerName: player.name,
                    // Unpriced rather than free. Zero is a price an operator may mean, so a
                    // hand-added player arriving at it would be a decision nobody made.
                    salary: null,
                    // Nothing derived them, so there is no model figure to compare against — and
                    // a hand-added player is an override by construction, which is what makes the
                    // reason compulsory. §4.5: the operator knows something the rules do not.
                    derivedSalary: null,
                    isOverridden: true,
                    overrideReason: null,
                    isUnproven: true,
                    displayRank: null,
                    metrics: null,
                    compositePercentile: null,
                    droppedByModel: false,
                  },
                ]),
              )
            }
          />
        )}
      </div>
    </BuilderSection>
  );
}

/**
 * §4.5's counts, reported separately rather than collapsed into one "pool size".
 *
 * The spec is explicit that a single number here is ambiguous in the way that costs an afternoon:
 * qualified is what its own table is quoted in, and what was cut afterwards — by the identity guard
 * and by the model's cap — is a different fact. The same three figures the salary designer's
 * preview reports, for the same reason.
 */
function DeriveSummary({ result }: { result: SalaryPoolPreview }) {
  const cut = result.qualified - result.poolSize;

  return (
    <div className="border border-line bg-ground/40 p-3">
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        <Figure label="Priced" value={result.poolSize} strong />
        <Figure label="Cleared the minimums" value={result.qualified} />
        <Figure label="Turned up at all" value={result.candidates} />
      </div>
      {cut > 0 ? (
        <p className="mt-1.5 text-[11px] leading-relaxed text-ink-800/55">
          {result.truncated > 0
            ? `The model's cap kept the best ${result.poolSize} and dropped ${result.truncated}.`
            : ''}
          {result.unidentifiable > 0
            ? ` ${result.unidentifiable} qualified but ${
                result.unidentifiable === 1
                  ? 'is not a player anybody can roster'
                  : 'are not players anybody can roster'
              } — the results name them, but not as a card this room holds.`
            : ''}
        </p>
      ) : null}
      {result.warnings.length > 0 ? (
        <ul className="mt-2 space-y-1 border-t border-line pt-2">
          {result.warnings.map((warning, i) => (
            <li key={i} className="flex gap-1.5 text-[11px] leading-relaxed text-warn">
              <span aria-hidden>!</span>
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * §4.5's pool-size rule, and the loudest thing in this section.
 *
 * <b>It fires at both boundaries and says which way to move</b>, because the failures are opposite:
 * too small and every team is the same, too large and most entrants finish on nothing. A pool
 * inside the range is told so rather than left unremarked — silence about what §4.5 calls the
 * single most important contest parameter reads as the screen having no opinion.
 *
 * Beside it, the one hard floor: a pool smaller than a roster accepts no entries at all, which the
 * server refuses at publish with `pool_too_small`.
 */
function PoolSizeNotice({ poolSize, rosterSize }: { poolSize: number; rosterSize: number }) {
  if (poolSize > 0 && poolSize < rosterSize) {
    return (
      <p
        role="alert"
        className="border border-line border-l-4 border-l-bad bg-surface p-3 text-[11.5px] leading-relaxed text-ink-900"
      >
        A roster is {rosterSize} players and only {poolSize} {poolSize === 1 ? 'is' : 'are'}{' '}
        draftable. Nobody could build a legal team, so the contest would take no entries at all —
        publishing is refused until this is fixed.
      </p>
    );
  }

  const warning = poolSizeWarning(poolSize);
  if (warning) {
    return (
      <p className="border border-line border-l-4 border-l-warn bg-surface p-3 text-[11.5px] leading-relaxed text-ink-900">
        {warning}
      </p>
    );
  }

  const good = poolSizeReassurance(poolSize);
  return good ? (
    <p className="text-[11.5px] leading-relaxed text-ink-800/60">{good}</p>
  ) : null;
}

function EmptyPool({ frozen, usingModel }: { frozen: boolean; usingModel: boolean }) {
  if (frozen) {
    return (
      <p className="border border-dashed border-line p-6 text-center text-[12px] text-ink-800/55">
        This contest published with an empty pool.
      </p>
    );
  }

  return (
    <div className="border border-dashed border-line p-6 text-center">
      <p className="text-[13px] font-medium text-ink-950">Nobody is draftable yet</p>
      <p className="mx-auto mt-1.5 max-w-md text-[11.5px] leading-relaxed text-ink-800/60">
        {usingModel
          ? `Run the model and it prices everybody who has played here recently. You then adjust the handful you disagree with — aim for ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM} players.`
          : `You are pricing by hand, so start by adding the players you want people to be able to draft. Aim for ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM} of them.`}
      </p>
    </div>
  );
}

function PoolTable({
  rows,
  unit,
  frozen,
  expanded,
  onToggle,
  onUpdate,
  onRemove,
}: {
  rows: readonly PoolRow[];
  unit: string;
  frozen: boolean;
  expanded: string | null;
  onToggle: (playerId: string) => void;
  onUpdate: (playerId: string, patch: Partial<PoolRow>) => void;
  onRemove: (playerId: string) => void;
}) {
  return (
    <div className="max-h-[32rem] overflow-y-auto border border-line">
      <table className="w-full text-left text-sm">
        <thead className="sticky top-0 z-10 bg-surface text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
          <tr className="border-b border-line">
            <th className="px-3 py-2 font-semibold">Player</th>
            <th className="px-3 py-2 text-right font-semibold">Model says</th>
            <th className="px-3 py-2 text-right font-semibold">Costs</th>
            {frozen ? null : <th className="w-8 px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <PoolRowView
              key={row.playerId}
              row={row}
              unit={unit}
              frozen={frozen}
              open={expanded === row.playerId}
              onToggle={() => onToggle(row.playerId)}
              onUpdate={(patch) => onUpdate(row.playerId, patch)}
              onRemove={() => onRemove(row.playerId)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PoolRowView({
  row,
  unit,
  frozen,
  open,
  onToggle,
  onUpdate,
  onRemove,
}: {
  row: PoolRow;
  unit: string;
  frozen: boolean;
  open: boolean;
  onToggle: () => void;
  onUpdate: (patch: Partial<PoolRow>) => void;
  onRemove: () => void;
}) {
  const reasonMissing = row.isOverridden && !row.overrideReason?.trim();

  return (
    <>
      <tr className="border-b border-line/60 align-top">
        <td className="px-3 py-2">
          {/* The row is the control and the button carries the semantics — `PointsBreakdown`'s
              arrangement. One toggle rather than two that can disagree. */}
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex items-baseline gap-1.5 text-left"
          >
            <span aria-hidden className="text-[10px] text-ink-800/35">
              {open ? '▾' : '▸'}
            </span>
            <span className="min-w-0 text-ink-950">{row.playerName}</span>
          </button>
          <p className="num mt-0.5 pl-4 text-[11px] text-ink-800/50">
            {row.metrics ? (
              <>
                {strengthLabel(row.compositePercentile)} ·{' '}
                {row.metrics.cashCount === 1 ? '1 cash' : `${row.metrics.cashCount} cashes`}
                {row.metrics.leaderboardPoints > 0
                  ? ` · ${formatPoints(row.metrics.leaderboardPoints)} pts`
                  : ''}
              </>
            ) : (
              // No working to show, so the slot says where the price came from instead. That is
              // `poolRowProvenance`'s answer and never this component's: deciding it here from
              // whatever happens to be loaded is exactly what labelled every reopened pool
              // "Added by hand" — and the same answer is owed to the drill-down below.
              <span className="text-ink-800/40">{poolRowProvenance(row).label}</span>
            )}
          </p>
          <div className="mt-0.5 flex flex-wrap gap-x-2 pl-4">
            {/* §4.3: unproven means the model cannot tell this player apart, not that they are
                cheap. True of most of any real pool, which is the point rather than a fault. */}
            {row.isUnproven ? (
              <span
                title="The model has nothing to tell this player apart from the rest of the floor."
                className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-800/40"
              >
                Unproven
              </span>
            ) : null}
            {row.isOverridden ? (
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-signal">
                Priced by hand
              </span>
            ) : null}
          </div>
        </td>

        <td className="num px-3 py-2 text-right text-ink-800/55">
          {row.derivedSalary === null ? '—' : formatSalary(row.derivedSalary)}
        </td>

        <td className="px-3 py-2 text-right">
          {frozen ? (
            <span className="num font-semibold text-ink-950">
              {row.salary === null ? '—' : formatSalary(row.salary)}
            </span>
          ) : (
            <NumberInput
              value={row.salary}
              min={0}
              ariaLabel={`Price for ${row.playerName}, in ${unit}`}
              invalid={row.salary === null || row.salary < 0 || reasonMissing}
              className="w-24 text-right"
              onChange={(salary) => {
                // Editing the price *is* the override, and returning it to what the model said
                // clears it again — so an operator who changes their mind is not left with a
                // reason box demanding an explanation for agreeing with the model. An empty box
                // is neither: it is a row still wanting a price, and it stays overridden so the
                // reason it will need is already on screen.
                const backToDerived =
                  salary !== null &&
                  row.derivedSalary !== null &&
                  Math.abs(salary - row.derivedSalary) < 0.005;
                onUpdate({
                  salary,
                  isOverridden: !backToDerived,
                  overrideReason: backToDerived ? null : row.overrideReason,
                });
              }}
            />
          )}
        </td>

        {frozen ? null : (
          <td className="px-3 py-2 text-right">
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${row.playerName} from the pool`}
              title="Remove from the pool — for somebody who will not be at this series."
              className="text-[11px] text-ink-800/40 transition hover:text-chip-500"
            >
              ✕
            </button>
          </td>
        )}
      </tr>

      {row.isOverridden && !frozen ? (
        <tr className="border-b border-line/60">
          <td colSpan={4} className="px-3 pb-3">
            <label className="block pl-4">
              <span className="mb-1 block text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
                Why this price? <span className="text-signal">Required</span>
              </span>
              <input
                type="text"
                value={row.overrideReason ?? ''}
                maxLength={500}
                placeholder="Not attending this series — priced down so nobody wastes a slot"
                onChange={(e) => onUpdate({ overrideReason: e.target.value })}
                className={`${controlClass} w-full ${reasonMissing ? 'border-bad' : ''}`}
              />
              <span className="mt-1 block text-[10.5px] text-ink-800/45">
                Kept with the contest for good. It is what answers “why did you make him cheap” a
                month from now.
              </span>
            </label>
          </td>
        </tr>
      ) : null}

      {open ? <MetricsRow row={row} /> : null}
    </>
  );
}

/**
 * What the price was built from — §9.1's "percentile inputs behind it", in facts.
 *
 * <b>Every figure here is a raw value the model read, never a rank and never the composite.</b>
 * §9.3 forbids a raw percentile and is right to: it is a weighted sum of percentile ranks, and an
 * operator arguing with one would be arguing with an abstraction. What answers "is this price
 * defensible" is that the player cashed twice, made a final table and won nothing — which is what
 * this shows, labelled with the same names the salary model builder uses for its inputs so the two
 * screens name one thing one way.
 */
function MetricsRow({ row }: { row: PoolRow }) {
  // Somebody opened this row wanting the figures and there are none. The sentence explaining that
  // is the provenance's, from the same call the subtitle makes — two readers of one row must not be
  // able to disagree about where its price came from, which is how one of them came to be wrong.
  if (!row.metrics) {
    return (
      <tr className="border-b border-line/60 bg-ground/30">
        <td colSpan={4} className="px-3 py-3 pl-7 text-[11.5px] leading-relaxed text-ink-800/60">
          {poolRowProvenance(row).detail}
        </td>
      </tr>
    );
  }

  const m = row.metrics;

  return (
    <tr className="border-b border-line/60 bg-ground/30">
      <td colSpan={4} className="px-3 py-3 pl-7">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11.5px] sm:grid-cols-3">
          <Metric label={SALARY_SOURCE_LABELS.cashCount} value={String(m.cashCount)} />
          <Metric
            label={SALARY_SOURCE_LABELS.leaderboardPoints}
            value={formatPoints(m.leaderboardPoints)}
          />
          <Metric label={SALARY_SOURCE_LABELS.totalEarnings} value={formatMoney(m.totalEarnings)} />
          <Metric label={SALARY_SOURCE_LABELS.eventsEntered} value={String(m.eventsEntered)} />
          <Metric
            label={SALARY_SOURCE_LABELS.bestFinish}
            // Null is "never cashed", which is not a finish of zero and not a missing figure.
            value={m.bestFinish === null ? 'No cash' : ordinal(m.bestFinish)}
          />
          <Metric label={SALARY_SOURCE_LABELS.firstPlaces} value={String(m.firstPlaces)} />
        </dl>
        <p className="mt-2 text-[11px] leading-relaxed text-ink-800/50">
          Read over the series the model was told to price from. Which of these actually moved the
          price, and by how much, is set by the weights in the model itself.
        </p>
      </td>
    </tr>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">{label}</dt>
      <dd className="num mt-0.5 text-ink-950">{value}</dd>
    </div>
  );
}

/**
 * §4.5's other half: "the operator adds or removes individuals".
 *
 * A rule cannot know that somebody has moved away, or that a visiting professional has confirmed
 * for the whole series. Both are ordinary, and both are why the pool is rule-derived *and*
 * operator-adjustable rather than either alone.
 *
 * Searched rather than listed: a room's roster runs to thousands, and the operator adding somebody
 * has a specific person in mind. The server searches (`POST …/players/search`, performance rank 2),
 * from two characters on; what to show from its answer is `pickerState`'s, as for the results editor.
 * Anybody already priced is left out of the results rather than offered and refused — but a match
 * that is already in says so, since silence reads as "no such player" and sends somebody off to look
 * for a spelling mistake.
 */
function AddPlayer({
  roomId,
  alreadyIn,
  unit,
  onAdd,
}: {
  roomId: string | null;
  alreadyIn: ReadonlySet<string>;
  unit: string;
  onAdd: (player: PlayerSummary) => void;
}) {
  const [query, setQuery] = useState('');
  const term = query.trim();
  const settled = useDebouncedValue(term, 250);
  const search = useRoomPlayerSearch(roomId, settled, settled.length >= 2);
  const { status, available: matches, alreadyAdded } = pickerState({
    term,
    found: search.data,
    excludeIds: [...alreadyIn],
    pending: settled !== term || search.isPlaceholderData,
    failed: search.isError,
  });
  const alreadyMatched = alreadyAdded.length;

  return (
    <div className="border border-line p-3">
      <label className="block">
        <span className="mb-1 block text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
          Add somebody the model missed
        </span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={PLAYER_SEARCH_MAX_LENGTH}
          placeholder="Name or card number"
          className={`${controlClass} w-full`}
        />
      </label>

      {term.length >= 2 ? (
        status === 'searching' ? (
          <p className="mt-2 text-[11.5px] text-ink-800/55">Searching…</p>
        ) : status === 'failed' ? (
          <p className="mt-2 text-[11.5px] text-red-700">Couldn't search the room's players. Try again.</p>
        ) : matches.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {matches.map((player) => (
              <li key={player.id}>
                <button
                  type="button"
                  onClick={() => {
                    onAdd(player);
                    setQuery('');
                  }}
                  className="flex w-full items-baseline justify-between gap-3 border border-line bg-surface px-2.5 py-1.5 text-left text-[12.5px] transition hover:border-ink-800/40"
                >
                  <span className="min-w-0 truncate text-ink-950">{player.name}</span>
                  <span className="num shrink-0 text-[11px] text-ink-800/50">{player.cardId}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-[11.5px] text-ink-800/55">
            {alreadyMatched > 0
              ? `${alreadyMatched === 1 ? 'That player is' : 'Those players are'} already in the pool.`
              : 'No card at this room matches that. A contest can only price players this room has carded.'}
          </p>
        )
      ) : null}

      <p className="mt-2 text-[11px] leading-relaxed text-ink-800/50">
        Somebody added here arrives with no price — set one in {unit} and say why, the same as any
        other price you set by hand.
      </p>
    </div>
  );
}

function Figure({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) {
  return (
    <div>
      <div
        className={`num font-figure leading-none ${
          strong ? 'text-lg font-semibold text-ink-950' : 'text-lg text-ink-800/70'
        }`}
      >
        {value.toLocaleString('en-US')}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-[0.08em] text-ink-800/45">{label}</div>
    </div>
  );
}
